import { CLUSTERS } from '../../constants'
import { CLUSTER_NAMES, clusterFor, useServer } from '../../operations/use-server'
import { describeWorkspace, loadAuth } from '../../project/auth'
import { getServerUrl } from '../../project/config'
import { cliCommand } from '../../utils/invocation'
import { fail } from '../fail'
import { say, section } from '../output'

/**
 * The list, shown when `use` is given nothing to switch to.
 *
 * Marking the current one and naming the workspace it is logged in to is the whole point: "which cluster am
 * I about to deploy to" is the question this command exists to answer, and it should be answerable without
 * changing anything.
 */
function listServers(): void {
  const current = getServerUrl().replace(/\/+$/, '')
  const stored = loadAuth()
  const width = Math.max(...CLUSTER_NAMES.map(name => name.length))

  section('Servers:')
  say('')

  for (const name of CLUSTER_NAMES) {
    const url = CLUSTERS[name]
    const marker = url === current
      ? `   ← current${ stored?.workspaceId ? `, logged in to ${ describeWorkspace(stored, { short: true }) }` : '' }`
      : ''

    say(`  ${ name.padEnd(width) }  ${ url }${ marker }`)
  }

  // A project pointed somewhere that is not a known cluster still has to be able to see where it points.
  if (!clusterFor(current)) {
    say(`  ${ '·'.padEnd(width) }  ${ current }   ← current (not one of the named clusters)`)
  }

  section(`  ${ cliCommand() } use <name>   — switch, detaching the current login`)
  say('')
}

export async function useCommand(target?: string): Promise<void> {
  if (!target) {
    listServers()

    return
  }

  try {
    const result = useServer(target)
    const where = result.cluster ? `${ result.cluster } — ${ result.serverUrl }` : result.serverUrl

    if (!result.changed) {
      section(`  Already using ${ where }.`)
      say('')

      return
    }

    section(`  Switched to ${ where }.`)

    // Named explicitly: the credentials that were dropped belonged to a *different* server, and saying so
    // is what stops this reading like the CLI logged you out for no reason.
    if (result.clearedLogin || result.clearedWorkspaceId) {
      say(`  Detached the login for ${ result.previousServerUrl || 'the previous server' }.`)
    }

    say(`  Run \`${ cliCommand() } login\` to connect this project to a workspace.`)
    say('')
  } catch (error) {
    fail(error)
  }
}
